import React from 'react';
import { Icon, IconKey } from '/imports/ui/Icon';
import { panelSurface } from '/imports/ui/Panel';

export type StatTrend = 'up' | 'down' | 'flat';

export interface StatProps {
  label: string;
  value: React.ReactNode;
  /** Small qualifier under the value, e.g. 'last 7 days'. */
  hint?: string;
  icon?: IconKey;
  trend?: StatTrend;
  /** Formatted delta shown beside the trend arrow, e.g. '+12%'. */
  delta?: string;
  /** Explains what the number means. Shown on hover, not as visible clutter. */
  info?: string;
  /** Makes the tile a control. Omit and it stays inert, as most tiles should. */
  onClick?(): void;
  className?: string;
}

/**
 * A single KPI tile.
 *
 * Direction is never carried by colour alone: the arrow glyph says which way
 * the number moved, so the tile still reads correctly in monochrome and to
 * anyone who cannot separate the green from the red.
 *
 * `up` is deliberately not hardwired to green. Rising escrow volume is good and
 * a rising cancel rate is not, so the caller passes the tone it means via
 * `trend` and the semantic buy/sell tokens do the rest.
 */
const trendIcon: Record<StatTrend, IconKey> = {
  up: 'arrowUp',
  down: 'arrowDown',
  flat: 'remove',
};

const trendClass: Record<StatTrend, string> = {
  up: 'text-buy',
  down: 'text-sell',
  flat: 'text-fg-3',
};

export const Stat = ({ label, value, hint, icon, trend, delta, info, onClick, className }: StatProps) => (
  /*
   * Interactive only when a handler is given. A tile that looks clickable and
   * is not is worse than one that never invites the click, so the cursor,
   * hover and keyboard affordances all appear together or not at all.
   */
  <div
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    title={info}
    className={`stat ${panelSurface} flex min-w-0 flex-col gap-1.5 p-4${onClick ? ' cursor-pointer hover:border-strong' : ''}${className ? ` ${className}` : ''}`}
  >
    <div className="flex items-center gap-1.5">
      {icon && <Icon icon={icon} size={14} className="shrink-0 text-fg-3" />}
      <span className="eyebrow truncate text-fg-3">{label}</span>
      {/* A quiet marker that hovering will explain the metric. */}
      {info && <span className="shrink-0 text-fg-3" aria-hidden="true"><Icon icon="info" size={12} /></span>}
    </div>
    {/* Tabular figures so a column of tiles does not jitter as values update. */}
    <div className="mt-0.5 font-mono text-[26px] leading-none font-semibold tabular-nums text-fg-1">{value}</div>
    {(hint || (trend && delta)) && (
      <div className="flex items-center gap-1.5 text-[12px]">
        {trend && delta && (
          <span className={`inline-flex items-center gap-0.5 font-semibold ${trendClass[trend]}`}>
            <Icon icon={trendIcon[trend]} size={12} />
            {delta}
          </span>
        )}
        {hint && <span className="truncate text-fg-3">{hint}</span>}
      </div>
    )}
  </div>
);

/** Responsive row of tiles. Tiles stay readable down to a phone width. */
export const StatRow = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <div className={`stat-row stat-track grid gap-3${className ? ` ${className}` : ''}`}>
    {children}
  </div>
);
